/**
 * GridCrash — Unlock Engine
 *
 * Tracks lifetime score across all games and unlocks emoji mascots
 * every 500 points up to 25,000, then every 5,000 points up to 100,000.
 * Persists in localStorage with in-memory fallback.
 *
 * Unlock progression (65 total — every 500 pts up to 25,000, then every 5,000 pts up to 100,000):
 *   0      → 😐  Neutral        (always unlocked, default)
 *   500    → 🙂  Hopeful
 *   1000   → 😎  Cool
 *   1500   → 🤔  Thinking
 *   2000   → 😤  Determined
 *   2500   → 🧠  Big Brain
 *   3000   → 🔥  On Fire
 *   3500   → ⚡  Electric
 *   4000   → 💀  Fearless
 *   4500   → 🎯  Sharpshooter
 *   5000   → 🏆  Champion
 *   5500   → 👑  Royalty
 *   6000   → 🌟  All-Star
 *   6500   → 🚀  Rocket
 *   7000   → 💎  Diamond
 *   7500   → 🌈  Rainbow
 *   8000   → 🎰  Jackpot
 *   8500   → 🧬  Genius
 *   9000   → 🌌  Cosmic
 *   9500   → 👾  Legend
 *   10000  → 🦊  Fox
 *   10500  → 🐉  Dragon
 *   11000  → 🦋  Butterfly
 *   11500  → 🌊  Wave
 *   12000  → 🎭  Masks
 *   12500  → 🔮  Crystal Ball
 *   13000  → 🦄  Unicorn
 *   13500  → 🌋  Volcano
 *   14000  → 🎪  Circus
 *   14500  → 🧿  Evil Eye
 *   15000  → 🦁  Lion
 *   15500  → 🐺  Wolf
 *   16000  → 🦅  Eagle
 *   16500  → 🐲  Dragon Face
 *   17000  → 🌠  Shooting Star
 *   17500  → ⚗️  Alchemy
 *   18000  → 🧲  Magnet
 *   18500  → 🔱  Trident
 *   19000  → ☄️  Comet
 *   19500  → 🌀  Cyclone
 *   20000  → 👁️  Eye
 *   20500  → 🗝️  Key
 *   21000  → ⚔️  Swords
 *   21500  → 🛡️  Shield
 *   22000  → 🏹  Bow
 *   22500  → 🔥💀 Inferno (skull fire combo)
 *   23000  → 🧨  Firecracker
 *   23500  → 💥  Explosion
 *   24000  → ⚡💎 Thunder Diamond
 *   24500  → 🌌👾 Void
 *   25000  → 🎖️  Medal of Honor
 *   30000  → 🎇  Fireworks
 *   35000  → 🦩  Flamingo
 *   40000  → 🦕  Dino
 *   45000  → 🛸  UFO
 *   50000  → 👽  Alien
 *   55000  → 🧚  Fairy
 *   60000  → 🧜  Mermaid
 *   65000  → 🦑  Kraken
 *   70000  → 🌪️  Tornado
 *   75000  → 🛰️  Satellite
 *   80000  → 💣  Bomb
 *   85000  → 🏔️  Summit
 *   90000  → 🌅  Sunrise
 *   95000  → 🦂  Scorpion
 *   100000 → 🐐  GOAT
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnlockResult {
  /** Updated lifetime total after adding points */
  newTotal: number;
  /** All currently unlocked emojis in order */
  unlocked: string[];
  /** Emojis newly unlocked this game (empty if none) */
  justUnlocked: string[];
  /** Points needed for the next unlock (0 if all unlocked) */
  nextThreshold: number;
  /** How far through the current tier (0–1) */
  tierProgress: number;
}

export interface UnlockState {
  lifetimeScore: number;
  unlocked: string[];
  active: string;
}

// ─── Unlock Table ─────────────────────────────────────────────────────────────

/** All unlockable emojis in order. Index 0 is always unlocked. */
export const UNLOCK_TABLE: Array<{ threshold: number; emoji: string; name: string }> = [
  { threshold: 0,     emoji: "😐", name: "Neutral"         },
  { threshold: 500,   emoji: "🙂", name: "Hopeful"         },
  { threshold: 1000,  emoji: "😎", name: "Cool"            },
  { threshold: 1500,  emoji: "🤔", name: "Thinking"        },
  { threshold: 2000,  emoji: "😤", name: "Determined"      },
  { threshold: 2500,  emoji: "🧠", name: "Big Brain"       },
  { threshold: 3000,  emoji: "🔥", name: "On Fire"         },
  { threshold: 3500,  emoji: "⚡", name: "Electric"        },
  { threshold: 4000,  emoji: "💀", name: "Fearless"        },
  { threshold: 4500,  emoji: "🎯", name: "Sharpshooter"    },
  { threshold: 5000,  emoji: "🏆", name: "Champion"        },
  { threshold: 5500,  emoji: "👑", name: "Royalty"         },
  { threshold: 6000,  emoji: "🌟", name: "All-Star"        },
  { threshold: 6500,  emoji: "🚀", name: "Rocket"          },
  { threshold: 7000,  emoji: "💎", name: "Diamond"         },
  { threshold: 7500,  emoji: "🌈", name: "Rainbow"         },
  { threshold: 8000,  emoji: "🎰", name: "Jackpot"         },
  { threshold: 8500,  emoji: "🧬", name: "Genius"          },
  { threshold: 9000,  emoji: "🌌", name: "Cosmic"          },
  { threshold: 9500,  emoji: "👾", name: "Legend"          },
  { threshold: 10000, emoji: "🦊", name: "Fox"             },
  { threshold: 10500, emoji: "🐉", name: "Dragon"          },
  { threshold: 11000, emoji: "🦋", name: "Butterfly"       },
  { threshold: 11500, emoji: "🌊", name: "Wave"            },
  { threshold: 12000, emoji: "🎭", name: "Masks"           },
  { threshold: 12500, emoji: "🔮", name: "Crystal Ball"    },
  { threshold: 13000, emoji: "🦄", name: "Unicorn"         },
  { threshold: 13500, emoji: "🌋", name: "Volcano"         },
  { threshold: 14000, emoji: "🎪", name: "Circus"          },
  { threshold: 14500, emoji: "🧿", name: "Evil Eye"        },
  { threshold: 15000, emoji: "🦁", name: "Lion"            },
  { threshold: 15500, emoji: "🐺", name: "Wolf"            },
  { threshold: 16000, emoji: "🦅", name: "Eagle"           },
  { threshold: 16500, emoji: "🐲", name: "Dragon Face"     },
  { threshold: 17000, emoji: "🌠", name: "Shooting Star"   },
  { threshold: 17500, emoji: "⚗️", name: "Alchemy"         },
  { threshold: 18000, emoji: "🧲", name: "Magnet"          },
  { threshold: 18500, emoji: "🔱", name: "Trident"         },
  { threshold: 19000, emoji: "☄️", name: "Comet"           },
  { threshold: 19500, emoji: "🌀", name: "Cyclone"         },
  { threshold: 20000, emoji: "👁️", name: "Eye"             },
  { threshold: 20500, emoji: "🗝️", name: "Key"             },
  { threshold: 21000, emoji: "⚔️", name: "Swords"          },
  { threshold: 21500, emoji: "🛡️", name: "Shield"          },
  { threshold: 22000, emoji: "🏹", name: "Bow"             },
  { threshold: 22500, emoji: "🧨", name: "Firecracker"     },
  { threshold: 23000, emoji: "💥", name: "Explosion"       },
  { threshold: 23500, emoji: "🔥", name: "Inferno"         },
  { threshold: 24000, emoji: "🌑", name: "Dark Moon"       },
  { threshold: 24500, emoji: "🕳️", name: "Void"            },
  { threshold: 25000, emoji: "🎖️", name: "Medal of Honor"  },
  { threshold: 30000, emoji: "🎇", name: "Fireworks"       },
  { threshold: 35000, emoji: "🦩", name: "Flamingo"        },
  { threshold: 40000, emoji: "🦕", name: "Dino"            },
  { threshold: 45000, emoji: "🛸", name: "UFO"             },
  { threshold: 50000, emoji: "👽", name: "Alien"           },
  { threshold: 55000, emoji: "🧚", name: "Fairy"           },
  { threshold: 60000, emoji: "🧜", name: "Mermaid"         },
  { threshold: 65000, emoji: "🦑", name: "Kraken"          },
  { threshold: 70000, emoji: "🌪️", name: "Tornado"         },
  { threshold: 75000, emoji: "🛰️", name: "Satellite"       },
  { threshold: 80000, emoji: "💣", name: "Bomb"            },
  { threshold: 85000, emoji: "🏔️", name: "Summit"          },
  { threshold: 90000, emoji: "🌅", name: "Sunrise"         },
  { threshold: 95000, emoji: "🦂", name: "Scorpion"        },
  { threshold: 100000, emoji: "🐐", name: "GOAT"           },
];

const UNLOCK_INTERVAL = 500; // points between each unlock

// ─── Storage Keys ────────────────────────────────────────────────────────────

const KEYS = {
  lifetime: "gridcrash.unlocks.lifetimeScore",
  unlocked: "gridcrash.unlocks.unlocked",
  active:   "gridcrash.unlocks.active",
} as const;

// ─── In-memory fallback ───────────────────────────────────────────────────────

let memState: UnlockState | null = null;

// ─── Purchased mascot packs ───────────────────────────────────────────────────

/**
 * Store-bought mascot packs extend the picker beyond score unlocks.
 * Injected as a provider (main.ts wires it to catalog.ts at boot) to
 * avoid an import cycle: profile.ts already imports this module.
 */
let extraEmojiProvider: () => string[] = () => [];

export function setExtraEmojiProvider(provider: () => string[]): void {
  extraEmojiProvider = provider;
}

function extraEmojis(): string[] {
  try { return extraEmojiProvider(); } catch { return []; }
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

function readState(): UnlockState {
  // Try memory cache first
  if (memState) return memState;

  try {
    const lifetime = parseInt(localStorage.getItem(KEYS.lifetime) ?? "0", 10);
    const rawUnlocked = localStorage.getItem(KEYS.unlocked);
    const unlocked: string[] = rawUnlocked
      ? (JSON.parse(rawUnlocked) as unknown[]).filter(
          (e): e is string => typeof e === "string"
        )
      : [UNLOCK_TABLE[0].emoji]; // always have neutral
    const active = localStorage.getItem(KEYS.active) ?? UNLOCK_TABLE[0].emoji;

    const state: UnlockState = {
      lifetimeScore: isNaN(lifetime) ? 0 : lifetime,
      unlocked,
      active,
    };
    memState = state;
    return state;
  } catch {
    const fallback: UnlockState = {
      lifetimeScore: 0,
      unlocked: [UNLOCK_TABLE[0].emoji],
      active: UNLOCK_TABLE[0].emoji,
    };
    memState = fallback;
    return fallback;
  }
}

function writeState(state: UnlockState): void {
  memState = state;
  try {
    localStorage.setItem(KEYS.lifetime, String(state.lifetimeScore));
    localStorage.setItem(KEYS.unlocked, JSON.stringify(state.unlocked));
    localStorage.setItem(KEYS.active, state.active);
  } catch {
    // Private browsing or quota — memory fallback already updated
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Add points to lifetime score and check for new unlocks.
 * Call this after every game with the final score.
 * Returns what changed so the UI can react.
 */
export function addToLifetimeScore(points: number): UnlockResult {
  if (points <= 0) return buildResult(readState(), []);

  const state = readState();
  const prevTotal = state.lifetimeScore;
  const newTotal  = prevTotal + points;
  state.lifetimeScore = newTotal;

  // Check each unlock threshold
  const justUnlocked: string[] = [];
  for (const entry of UNLOCK_TABLE) {
    if (
      entry.threshold > 0 &&           // skip the default emoji
      newTotal >= entry.threshold &&
      prevTotal < entry.threshold &&   // newly crossed this threshold
      !state.unlocked.includes(entry.emoji)
    ) {
      state.unlocked.push(entry.emoji);
      justUnlocked.push(entry.emoji);
    }
  }

  // If something was newly unlocked, auto-set active to the latest unlock
  if (justUnlocked.length > 0) {
    state.active = justUnlocked[justUnlocked.length - 1];
  }

  writeState(state);
  return buildResult(state, justUnlocked);
}

/** Get the currently active emoji for display in the header. */
export function getActiveEmoji(): string {
  return readState().active;
}

/**
 * Set the active emoji. Must be already unlocked.
 * Returns true if set, false if emoji not yet unlocked.
 */
export function setActiveEmoji(emoji: string): boolean {
  const state = readState();
  if (!state.unlocked.includes(emoji) && !extraEmojis().includes(emoji)) return false;
  state.active = emoji;
  writeState(state);
  return true;
}

/** All unlocked emojis in unlock order, plus purchased mascot packs. */
export function getUnlockedEmojis(): string[] {
  const unlocked = readState().unlocked;
  return [...unlocked, ...extraEmojis().filter((e) => !unlocked.includes(e))];
}

/** Current lifetime score total. */
export function getLifetimeScore(): number {
  return readState().lifetimeScore;
}

/**
 * Full unlock state — used by the picker modal to render
 * progress bar and locked/unlocked grid.
 */
export function getUnlockState(): UnlockState {
  return readState();
}

/**
 * Points needed to reach the next unlock.
 * Returns 0 if all emojis are already unlocked.
 */
export function getNextUnlockThreshold(): number {
  const { lifetimeScore } = readState();
  const next = UNLOCK_TABLE.find(e => e.threshold > lifetimeScore);
  return next ? next.threshold : 0;
}

/**
 * Progress through the current unlock tier (0–1).
 * Used to render the progress bar.
 *
 * Example: lifetime = 320, next threshold = 500
 *   tierStart = 250, tierEnd = 500, progress = (320-250)/(500-250) = 0.28
 */
export function getTierProgress(): number {
  const { lifetimeScore } = readState();

  // Find the tier we're currently in
  let tierStart = 0;
  let tierEnd   = UNLOCK_INTERVAL;

  for (let i = 0; i < UNLOCK_TABLE.length - 1; i++) {
    const curr = UNLOCK_TABLE[i].threshold;
    const next = UNLOCK_TABLE[i + 1].threshold;
    if (lifetimeScore >= curr && lifetimeScore < next) {
      tierStart = curr;
      tierEnd   = next;
      break;
    }
  }

  // If at or past max threshold
  if (lifetimeScore >= UNLOCK_TABLE[UNLOCK_TABLE.length - 1].threshold) {
    return 1;
  }

  const range = tierEnd - tierStart;
  if (range <= 0) return 1;
  return Math.min((lifetimeScore - tierStart) / range, 1);
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function buildResult(state: UnlockState, justUnlocked: string[]): UnlockResult {
  const next = getNextUnlockThreshold();
  return {
    newTotal:      state.lifetimeScore,
    unlocked:      [...state.unlocked],
    justUnlocked,
    nextThreshold: next,
    tierProgress:  getTierProgress(),
  };
}
